import {
    OperationVariables,
    QueryLazyOptions,
    useReactiveVar,
} from '@apollo/client'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { Typography, debounce } from '@material-ui/core'
import { stripDoubleSpacesTrimStart } from 'bpm/shared/utils/inputUtils'
import clsx from 'clsx'
import React, {
    ChangeEvent,
    FC,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react'
import { RadioType } from './AddProductTypes'
import {
    addProductDialogState,
    setProductDialogState,
} from './addProductFormHandler'
import { AutoCompleteInput } from './AutoCompleteInput'
import { useAddProductFormStyles } from './formStyles'
import { ProductTypeAutocompleteOption } from './ProductTypeAutocompleteOption'

type Props = {
    callAutoSuggest: (
        options?: QueryLazyOptions<OperationVariables> | undefined
    ) => void
    autoSuggestData: any
    autoSuggestLoading: boolean
    disabled: boolean
    sectionNames: string
    isFallbackResult: boolean
}

const ExistingProductTypeAutoCompleteInput: FC<Props> = ({
    callAutoSuggest,
    autoSuggestData,
    autoSuggestLoading,
    disabled,
    sectionNames,
    isFallbackResult,
}) => {
    const classes = useAddProductFormStyles()

    const { errors, type, productType } = useReactiveVar(
        addProductDialogState
    )

    const [searchText, setSearchText] = useState('')

    const callAutoSuggestQuery = useCallback(
        (fetchString) => {
            if (!type) return

            callAutoSuggest({
                variables: {
                    input: {
                        listingType: type,
                        familyName: sectionNames,
                        productType: fetchString,
                        isProductTypeSearch: true,
                        limit: 50,
                    },
                },
            })
        },
        [type, sectionNames, searchText, callAutoSuggest]
    )

    const callAutoSuggestQueryRef = useRef(callAutoSuggestQuery)
    callAutoSuggestQueryRef.current = callAutoSuggestQuery

    const debouncedCallAutoSuggestQuery = useMemo(
        () =>
            debounce(
                (value: string) => callAutoSuggestQueryRef.current(value),
                300
            ),
        []
    )

    const productTypeAutoSuggestOptions = useMemo(() => {
        const raw: string[] =
            autoSuggestData?.productListingDetailsAutoSuggest.productTypeDetails
                .reduce((acc: string[], curr: Record<string, string>) => {
                    acc.push(curr.formattedProductTypeName)
                    return acc
                }, [])
                .slice(0, 50) ?? []

        const isMoPt = (o: string) => {
            const parts = o.split('%|%')
            return parts.length > 1 && parts[1] !== 'null' && parts[1] !== ''
        }

        const collate = (a: string, b: string) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })

        const options = isFallbackResult
            ? [
                  ...raw.filter((o) => !isMoPt(o)).sort(collate),
                  ...raw.filter(isMoPt).sort(collate),
              ]
            : raw.sort(collate)

        if (
            productType &&
            !options.some((o: string) => o.split('%|%')[0] === productType)
        ) {
            return [productType, ...options]
        }
        return options
    }, [autoSuggestData, productType, isFallbackResult])

    const handleChange = useCallback(
        (_, value) => {
            const delimitedOption = value?.split('%|%')
            const tempValue =
                delimitedOption && delimitedOption?.length > 0
                    ? delimitedOption[0]
                    : ''
            setSearchText(tempValue)
            setProductDialogState({
                productType: tempValue,
                productTypeRadio: RadioType.Existing,
                errors: { ...errors, productType: '' },
            })
        },
        [setProductDialogState, setSearchText, errors]
    )

    const handleInputChange = useCallback(
        (e: ChangeEvent<{}>, value: string, reason?: string) => {
            if (e?.type === 'clear' || reason === 'reset') {
                setSearchText('')
                if (e?.type === 'clear') callAutoSuggestQuery('')
                return
            }

            if (e?.type === 'change') {
                const delimitedOption = value?.split('%|%')
                const tempValue =
                    delimitedOption && delimitedOption?.length > 0
                        ? delimitedOption[0]
                        : ''

                setSearchText(tempValue ?? '')
                setProductDialogState({
                    productType: '',
                    errors: { ...errors, productType: '' },
                })

                debouncedCallAutoSuggestQuery(
                    stripDoubleSpacesTrimStart(tempValue)
                )
            }
        },
        [
            setProductDialogState,
            errors,
            debouncedCallAutoSuggestQuery,
            stripDoubleSpacesTrimStart,
        ]
    )

    const handleOpen = useCallback(() => {
        callAutoSuggestQuery(stripDoubleSpacesTrimStart(productType) ?? '')
    }, [callAutoSuggestQuery, productType, stripDoubleSpacesTrimStart])

    const isNewProductTypeDisabled = useMemo(
        () =>
            !!searchText &&
            productTypeAutoSuggestOptions.some(
                (o: string) =>
                    o.split('%|%')[0] === searchText.trim().toUpperCase()
            ),
        [searchText, productTypeAutoSuggestOptions]
    )

    const handleCreateNewProductType = useCallback(() => {
        if (!searchText) return
        const upperValue = searchText.trim().toUpperCase()
        if (
            productTypeAutoSuggestOptions.some(
                (o: string) => o.split('%|%')[0] === upperValue
            )
        )
            return
        setProductDialogState({
            productType: upperValue,
            productTypeRadio: RadioType.New,
            errors: { ...errors, productType: '' },
        })
        setSearchText(upperValue)
    }, [searchText, errors, productTypeAutoSuggestOptions])

    const renderFooter = useCallback(
        () => (
            <Typography
                role="button"
                tabIndex={isNewProductTypeDisabled ? -1 : 0}
                data-testid="addNewProductTypeButton"
                className={clsx(classes.addNewOption, {
                    [classes.addNewOptionDisabled]: isNewProductTypeDisabled,
                })}
                onMouseDown={(e) => {
                    e.preventDefault()
                    handleCreateNewProductType()
                    ;(document.activeElement as HTMLElement)?.blur()
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleCreateNewProductType()
                    }
                }}
            >
                + Create New Product Type
            </Typography>
        ),
        [
            classes.addNewOption,
            classes.addNewOptionDisabled,
            handleCreateNewProductType,
            isNewProductTypeDisabled,
        ]
    )

    return (
        <GridItem
            className={clsx(classes.rowContainer, 'rowContainer')}
            data-testid="productType"
            xs={12}
        >
            <AutoCompleteInput
                clearOnBlur={true}
                name="productType"
                label="Product Type"
                disabled={disabled}
                valid={!errors.productType}
                handleOpen={handleOpen}
                handleChange={handleChange}
                handleInputChange={handleInputChange}
                selectedValue={productType}
                loading={autoSuggestLoading}
                items={productTypeAutoSuggestOptions ?? []}
                customOptions={(option: string) => (
                    <ProductTypeAutocompleteOption option={option} />
                )}
                getOptionLabel={(option) => option?.split('%|%')[0] ?? ''}
                renderFooter={renderFooter}
            />
        </GridItem>
    )
}
export default ExistingProductTypeAutoCompleteInput
